// src/pages/GiveAccess.jsx

import React, { useEffect, useState, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useSocket } from '../../../context/SocketContext';
import api from '../../../services/api.js';
import QRScanner from './components/QRScanner.js';
import UserSelectModal from './components/UserSelectModal.jsx';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import styles from './GiveAccess.module.css';

export default function GiveAccess() {
  const socket = useSocket();
  const [routes, setRoutes] = useState([]);
  const [filter, setFilter] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newRouteId, setNewRouteId] = useState(null);
  const sliderRef = useRef(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [modalRoute, setModalRoute] = useState(null);

  // Даты: неделю назад – сегодня
  const todayISO   = new Date().toISOString().split('T')[0];
  const weekAgoISO = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(todayISO);
  const [endDate, setEndDate]   = useState(todayISO);

  // Загрузка маршрутов
  useEffect(() => {
    const fetchRoutes = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetched = await api.get('/routes/by-date', {
          params: { startDate, endDate }
        });
        const arr = Array.isArray(fetched) ? fetched : [];
        setRoutes(arr);
        setCurrentIndex(0);
      } catch (e) {
        console.error('Ошибка загрузки маршрутов:', e);
        setError('Не удалось загрузить маршруты');
      } finally {
        setLoading(false);
      }
    };
    fetchRoutes();
  }, [startDate, endDate]);

  // Подписка на новые маршруты через сокет
  useEffect(() => {
    if (!socket) return;
    socket.emit('subscribe:routes');
    const handleNew = (route) => {
      setRoutes(prev => [route, ...prev]);
      setNewRouteId(route.id);
      setCurrentIndex(0);
      // через 5 сек уберём бейдж
      setTimeout(() => setNewRouteId(null), 5000);
    };
    socket.on('new_route_created', handleNew);
    return () => {
      socket.off('new_route_created', handleNew);
      socket.emit('unsubscribe:routes');
    };
  }, [socket]);



  const filtered = routes.filter(r => {
    const term = filter.toLowerCase();
    return (
      r.route_number.toString().includes(term) ||
      (r.driver || '').toLowerCase().includes(term) ||
      (r.loader1 || '').toLowerCase().includes(term) ||
      (r.loader2 || '').toLowerCase().includes(term)
    );
  });
  const currentList = filtered;
  const current = currentList[currentIndex] || {};

    useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'ArrowLeft') {
        setCurrentIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'ArrowRight') {
        setCurrentIndex(i => Math.min(i + 1, currentList.length - 1));
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [currentList.length]);

  // Открыть маршрут
  const handleOpen = async () => {
    try {
      await api.patch(`/routes/${current.id}/status`, { route_status: 'Открыт' });
      setRoutes(rs =>
        rs.map((r, i) =>
          i === currentIndex ? { ...r, route_status: 'Открыт' } : r
        )
      );
    } catch {
      alert('Не удалось открыть маршрут');
    }
  };

  // Сканирование QR для доступа
  const handleScan = async data => {
    setScanning(false);
    let payload;
    try {
      payload = JSON.parse(data);
    } catch {
      console.error('Неверный формат QR:', data);
      return;
    }
    const ids = (current.permittedUsers || []).map(u => u.id);
    if (ids.includes(payload.id)) {
      return alert(`Пользователь ${payload.username} уже имеет доступ`);
    }
    try {
      await api.post('/route-access/grant', {
        userId: payload.id,
        routeListId: current.id
      });
      setRoutes(rs =>
        rs.map((r, i) => i === currentIndex
          ? { 
              ...r, 
              permittedUsers: [...(r.permittedUsers || []), {
                id: payload.id,
                username: payload.username
              }]
            }
          : r
        )
      );
    } catch (e) {
      console.error('Ошибка выдачи доступа:', e);
      alert('Ошибка при выдаче доступа');
    }
  };

    // Ручное выдавание через внешнюю модалку
  const handleGrantManual = async (user, route) => {
    const ids = (route.permittedUsers || []).map(u => u.id);
    if (ids.includes(user.id)) {
      return alert(`Пользователь ${user.username} уже имеет доступ`);
    }
    try {
      await api.post('/route-access/grant', {
        userId: user.id,
        routeListId: route.id
      });
      setRoutes(rs =>
        rs.map(r =>
          r.id === route.id
            ? {
                ...r,
                permittedUsers: [...(r.permittedUsers || []), {
                  id: user.id,
                  username: user.username,
                  firstName: user.firstName || ''
                }]
              }
            : r
        )
      );
    } catch (e) {
      console.error('Ошибка выдачи доступа вручную:', e);
      alert('Не удалось выдать доступ');
    }
  };

  const handleRevoke = async (user, route) => {
    const ok = window.confirm(
      `Вы действительно хотите забрать доступ у пользователя ${user.username}?`
    );
    if (!ok) return;
    try {
      await api.delete('/route-access/revoke', {
        data: { userId: user.id, routeListId: route.id }
      });
      setRoutes(rs =>
        rs.map(r =>
          r.id === route.id
            ? {
                ...r,
                permittedUsers: (r.permittedUsers || []).filter(u => u.id !== user.id)
              }
            : r
        )
      );
    } catch (e) {
      console.error('Ошибка при отзыве доступа:', e);
      alert('Не удалось отозвать доступ');
    }
  };  

  // Свайпы
  const handlers = useSwipeable({
    onSwipedLeft:  () => setCurrentIndex(i => Math.min(i + 1, currentList.length - 1)),
    onSwipedRight: () => setCurrentIndex(i => Math.max(i - 1, 0)),
    trackMouse: true
  });

  // Скролл слайдера к текущему индексу (при появлении «Нового»)
  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.style.transform = `translateX(-${currentIndex * 100}%)`;
    }
  }, [currentIndex]);

    // Подсчёт прогресса
  const total = (current.receipts || []).length;
  const delivered = current.receipts?.filter(r => r.status === 'Закрыта').length || 0;
  const inProgress = current.receipts?.filter(r => r.status === 'Открыта').length || 0;
  const undelivered = current.receipts?.filter(r => r.status === 'Не доставлено').length || 0;

  if (loading) return <div className={styles.container}><p>Загрузка…</p></div>;
  if (error)   return <div className={styles.container}><p className={styles.empty}>{error}</p></div>;
  if (!currentList.length) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <input
            type="text"
            placeholder="Поиск по номеру, водителю, грузчику…"
            value={filter}
            onChange={e => {
              setFilter(e.target.value);
              setCurrentIndex(0); // сбрасываем прокрутку на первый
            }}
            className={styles.searchInput}
          />
            С: <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            По: <input type="date" value={endDate}   onChange={e => setEndDate(e.target.value)} />
        </header>
        <p className={styles.empty}>Нет маршрутов за выбранный период.</p>
      </div>
    );
  }

  return (
    <div className={styles.container} {...handlers}>
      {/* Фильтр: поиск и даты */}
      <header className={styles.header}>
        <input
          type="text"
          placeholder="Поиск по номеру, водителю, грузчику…"
          value={filter}
          onChange={e => {
            setFilter(e.target.value);
            setCurrentIndex(0);
          }}
          className={styles.searchInput}
        />
      <label className={styles.field}>
        С:
        <input
          type="date"
          className={styles.inputDate}
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
        />
      </label>
      <label className={styles.field}>
        По:
        <input
          type="date"
          className={styles.inputDate}
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
        />
      </label>
      </header>

      {/* Навигация и карточки */}
      <div className={styles.routeWrapper}>
        <button
          className={`${styles.navBtn} ${styles.left}`}
          onClick={() => setCurrentIndex(i => Math.max(i - 1, 0))}
          disabled={currentIndex === 0}
        >
          <BiChevronLeft size={32}/>
        </button>

        <div className={styles.sliderOuter}>
          <div className={styles.slider} ref={sliderRef}>
            {currentList.map((route, idx) => {
              // расчёт прогресса
              const total = (route.receipts || []).length;
              const deliveredCount   = (route.receipts || []).filter(r => r.status === 'Закрыта').length;
              const inProgressCount  = (route.receipts || []).filter(r => r.status === 'Открыта').length;
              const undeliveredCount = (route.receipts || []).filter(r => r.status === 'Не доставлено').length;
              const pct = total > 0
                ? n => (n / total) * 100
                : () => 0;

              return (
                <div key={route.id} className={styles.cardWrapper}>
                  <div className={`${styles.card} ${route.id === newRouteId ? styles.newBadge : ''}`}>
                    <div className={styles.cardContent}>
                      {route.id === newRouteId && <span className={styles.badge}>Новый</span>}
                      <h2 className={styles.title}>Маршрут №{route.route_number}</h2>
                      <p><strong>Дата:</strong> {route.route_date}</p>
                      <p><strong>Статус:</strong> {route.route_status}</p>
                      <p><strong>Районы:</strong> {route.route_districts || '—'}</p>
                      <p><strong>Водитель:</strong> {route.driver || '—'}</p>
                      <p><strong>Грузчики:</strong> {(route.loader1 || '—') + (route.loader2 ? `, ${route.loader2}` : '')}</p>

                      <div className={styles.progressContainer}>
                        <div
                          className={styles.progressDelivered}
                          style={{ width: `${pct(deliveredCount)}%` }}
                        />
                        <div
                          className={styles.progressUndelivered}
                          style={{ width: `${pct(undeliveredCount)}%` }}
                        />
                      </div>

                      <p>
                        <strong>Доставлено:</strong> {deliveredCount}/{total}
                      </p>
                      {/* Список пользователей с доступом */}
                      <p><strong>Кто с доступом:</strong></p>
                      <ul className={styles.userListMobile}>
                        {route.permittedUsers?.length > 0 ? (
                          route.permittedUsers.map(u => (
                            <li
                              key={u.id}
                              className={styles.userItemMobile}
                              onClick={() => handleRevoke(u, route)}
                            >
                              {u.username}
                            </li>
                          ))
                        ) : (
                          <li className={styles.userItemMobile}>—</li>
                        )}
                      </ul>
                    </div>

                    <div className={styles.actions}>
                      <button
                        className={`${styles.openBtn} ${route.route_status === 'Открыт' ? styles.openedBtn : ''}`}
                        onClick={handleOpen}
                        disabled={route.route_status === 'Открыт'}
                      >
                        {route.route_status === 'Открыт' ? 'Маршрут открыт' : 'Открыть маршрут'}
                      </button>
                      <div className={styles.accessButtonsWrapper}>
                        <button
                          className={styles.accessBtn}
                          onClick={() => setScanning(true)}
                        >
                          Дать доступ сканом
                        </button>
                        <button
                          className={styles.accessBtn}
                          onClick={() => {
                            setModalRoute(route);
                            setIsUserModalOpen(true);
                          }}
                        >
                          Дать доступ вручную
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <button
          className={`${styles.navBtn} ${styles.right}`}
          onClick={() => setCurrentIndex(i => Math.min(i + 1, currentList.length - 1))}
          disabled={currentIndex === currentList.length - 1}
        >
          <BiChevronRight size={32}/>
        </button>
      </div>

      {/* QR-сканер */}
      {scanning && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <button className={styles.modalClose} onClick={() => setScanning(false)}>
              &times;
            </button>
            <QRScanner onScan={handleScan} onClose={() => setScanning(false)} />
          </div>
        </div>
      )}
    {isUserModalOpen && modalRoute && (
      <UserSelectModal
        route={modalRoute}
        onClose={() => {
          setIsUserModalOpen(false);
          setModalRoute(null);
        }}
        onGrant={async (user, route) => {
          await handleGrantManual(user, route);
        }}
      />
    )}
    </div>
  );
}
